import { forkJoin, Observable, of, switchMap } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseDatasource } from './base.datasource';
import { Paginator } from '../models/paginator';
import { Organization } from '../models/organization';
import { User } from '../models/user';
import { Exercice, ExerciceData } from '../models/exercice';
import { ExerciceRow, exerciceOrderByOptions, ExercicesProvider } from '../../exercices/providers/exercices-provider';
import { Library } from "../models/library";

export class ExerciseDatasource extends BaseDatasource<ExerciceRow> {
    constructor(
        private exercisesProvider: ExercicesProvider
    ) {
        super();
    }

    private _makeRows({organization, libraryIds, library, user, fetchImage, fetchVideo, addRights, page, itemsPerPage, search, selectedCategoryIds, order, filter}:
                        {organization: Organization, libraryIds: number[], library?: Library, user: User | null, fetchImage: boolean, fetchVideo: boolean, addRights: boolean, page: number, itemsPerPage: number, search: string, selectedCategoryIds: number[], order?: exerciceOrderByOptions, filter?: string | null}) : Observable<void> {
      this.loadingSubject.next(true);
      const libs: number[] = library ? [...libraryIds, ...(library.id !== undefined ? [library.id] : [])] : libraryIds;
      let getRows = this.exercisesProvider.getExerciceRows(organization, libs, fetchImage, fetchVideo, addRights, page, itemsPerPage, search, selectedCategoryIds, order, filter);

      if (user !== null) {
        getRows = this.exercisesProvider.getExerciseRowsByUser(organization, libs, fetchImage, fetchVideo, addRights, page, itemsPerPage, search, selectedCategoryIds, order);
      }

      return getRows.pipe(
        switchMap((paginator: Paginator) => {
          const toFetch: Array<Observable<ExerciceRow>> = [];

          if (paginator.data.length === 0) {
            this.totalItems = 0;
            this.itemsPerPage = 0;
            this.subject.next([]);
            this.loadingSubject.next(false);
            return of(void 0);
          }

          paginator.data.forEach((exercise: ExerciceData) => {
            toFetch.push(this.exercisesProvider.e_toExerciceRow(organization, new Exercice(exercise), fetchImage, fetchVideo, addRights));
          });

          return forkJoin(toFetch).pipe(
            map((exerciseRows: ExerciceRow[]) => {
              paginator.data = exerciseRows;

              this.totalItems = paginator.total;
              this.itemsPerPage = paginator.per_page;
              this.subject.next(paginator.data);
            }),
            catchError(() => of(void 0)),
            finalize(() => this.loadingSubject.next(false))
          );
        }),
      );
    }

    public loadExerciseRows(organization: Organization, libraryIds: number[], library?: Library, fetchImage = true, fetchVideo = true,
                            addRights = true, page = 1, itemsPerPage: number = environment.itemsPerPage, search = '',
                            selectedCategoryIds: number[] = [], order?: exerciceOrderByOptions, filter?: string | null): Observable<void> {
      return this._makeRows({organization, libraryIds, library, user: null, fetchImage, fetchVideo, addRights, page, itemsPerPage, search, selectedCategoryIds, order, filter});
    }

    public loadExerciseRowsByUser(organization: Organization, user: User, libraryIds: number[], library?: Library, fetchImage = true, fetchVideo = true,
                                  addRights = true, page = 1, itemsPerPage: number = environment.itemsPerPage, search = '',
                                  selectedCategoryIds: number[] = [], order?: exerciceOrderByOptions): Observable<void> {
      return this._makeRows({organization, libraryIds, library, user, fetchImage, fetchVideo, addRights, page, itemsPerPage, search, selectedCategoryIds, order});
    }
}
